import React, { useRef, useEffect } from "react";
import { ScrollContainer, FixedElement, useGlobalState } from 'react-nice-scroll';
import 'react-nice-scroll/dist/styles.css';

import { ScrollProvider, useScrollDirection } from "./ScrollContext";


interface StyledScrollContainerProps {
    children: React.ReactNode;
    className?: string;
    damping?: number;
    thumbMinSize?: number;
    renderByPixels?: boolean;
    alwaysShowTracks?: boolean;
    continuousScrolling?: boolean;
    delegateTo?: EventTarget;
    activeSmoothScrollOnTouchDevice?: boolean;
    disableSmoothScroll?: boolean;
    hideFixedElementOnScroll?: boolean;
}

type FixedElementProps = React.ComponentProps<typeof FixedElement>;
interface StyledFixedElementProps extends FixedElementProps {
    className?: string;
}

export const StyledScrollContainerCtx: React.FC<StyledScrollContainerProps> = ({
    children,
    hideFixedElementOnScroll,
    ...props
}) => {
    const [smoothScrollBar] = useGlobalState('smoothScrollBar');
    const { setIsScrollingDown } = useScrollDirection();
    const lastScrollTop = useRef(0);

    useEffect(() => {
        const handleScroll = ({ offset }: { offset: { y: number } }) => {
            let scrollTop = offset.y;

            if (Math.abs(scrollTop - lastScrollTop.current) < 50) {
                return
            }

            if (scrollTop > lastScrollTop.current) {
                setIsScrollingDown(true); // Scrolling down
            } else {
                setIsScrollingDown(false); // Scrolling up
            }

            lastScrollTop.current = scrollTop;
        };

        if (!hideFixedElementOnScroll)
            return;

        if (!smoothScrollBar)
            return;

        smoothScrollBar.addListener(handleScroll);

        return () => {
            smoothScrollBar.removeListener(handleScroll);
        };
    }, [smoothScrollBar]);

    return (
        <ScrollContainer
            activeSmoothScrollOnTouchDevice={true}
            damping={0.75}
            {...(props as StyledScrollContainerProps)}
        >
            {children}
        </ScrollContainer>
    )
};

export const StyledScrollContainer: React.FC<StyledScrollContainerProps> = ({
    children,
    ...props
}) => {
    return (
        <ScrollProvider>
            <StyledScrollContainerCtx {...(props as StyledScrollContainerProps)}>
                {children}
            </StyledScrollContainerCtx>
        </ScrollProvider>
    )
};

export const StyledFixedElement: React.FC<StyledFixedElementProps> = ({
    children,
    ...props
}) => {
    const { isScrollingDown } = useScrollDirection();

    const dynamicStyles = {
        transform: isScrollingDown ? "translateY(-100%)" : "translateY(0)",
        opacity: isScrollingDown ? 0 : 1,
        transition: "transform 0.5s ease-in-out, opacity 0.5s ease-in-out",
    };

    return (
        <div style={dynamicStyles} className={props?.className}>
            <FixedElement {...(props as FixedElementProps)}>
                {children}
            </FixedElement>
        </div>
    )
};
